import { useCallback, useEffect, useRef, useState } from 'react';
import { isEmpty, get, find } from 'lodash';
import { useMutation, useLazyQuery } from '@apollo/client';
import { logError } from 'commons/logger';
import { setItemInStorage } from 'commons/localStorage';
import { MODULAR_ORDER_STATE } from 'commons/constants';
import getZipcode from 'commons/getZipcode';
import { ADD_MULTIPLE_TO_CART } from '@/graphql/mutations';
import { GET_CART } from '@/graphql/queries';
import avo from 'analytics/avo';
import useToggleCart from 'global-state/isCartOpen/useToggleCart';
import usePDPOptions from 'components/consumer/PDP/state/context/usePDPOptions';
import useModularPDPState from '../useModularPDPState';
import { getFinalPrice, getProductOptionsForAddToCart } from './utils';
const useModularPDPCart = () => {
  const [addingToCart, setAddingToCart] = useState(false);
  const {
    loading: modularPDPLoading,
    data: {
      modularState
    },
    actions: {
      updateModularOrderStatus,
      updateModularOrderTotal
    }
  } = useModularPDPState();
  const [getCart, {
    data: cartData
  }] = useLazyQuery(GET_CART, {
    fetchPolicy: 'network-only'
  });
  const [addMultipleToCart] = useMutation(ADD_MULTIPLE_TO_CART, {
    onCompleted: () => {
      getCart({
        variables: {
          zipCode: getZipcode()
        }
      });
    }
  });
  const toggleCart = useToggleCart();
  const {
    loading: optionsLoading,
    data: {
      options,
      selectedOptions
    }
  } = usePDPOptions();
  const {
    modularBundleOrderStatus,
    modularTotalPrice
  } = modularState || {};
  const orderStatusRef = useRef(modularBundleOrderStatus);
  orderStatusRef.current = modularBundleOrderStatus;
  const modularStateRef = useRef(modularState);
  modularStateRef.current = modularState;
  const updateModularOrder = ({
    productDetails,
    orientation = {}
  }) => {
    const finalKey = !isEmpty(orientation) && orientation.id ? `${productDetails.id}-${orientation.id}` : productDetails.id;
    // Add product to state
    const updatedBundleProducts = {
      ...modularBundleOrderStatus
    };
    const finalProductDetails = {
      ...productDetails
    };
    updatedBundleProducts[finalKey] = {
      ...finalProductDetails
    };
    if (finalProductDetails.quantity === 0) {
      delete updatedBundleProducts[finalKey];
    }
    // Update order status in local storage
    setItemInStorage(MODULAR_ORDER_STATE, updatedBundleProducts);
    updateModularOrderStatus(updatedBundleProducts);
  };
  const rebuildOrderFromScene = useCallback((activeProductsData, skuToIdMapping) => {
    const {
      bundleProducts
    } = modularStateRef.current || {};
    const sceneOrder = {};
    activeProductsData.forEach(marxentProduct => {
      const baseSku = marxentProduct.sku.split('|')[0];
      const skuParts = baseSku.split('-');
      const productSku = skuParts[0];
      const orientationSku = skuParts[1] ? `-${skuParts.slice(1).join('-')}` : null;
      const productId = skuToIdMapping[productSku];
      if (!productId) return;
      const orientationId = orientationSku && bundleProducts ? bundleProducts.find(p => p.id === productId && !isEmpty(p.orientation) && p.orientation.currentSelection.sku === orientationSku)?.orientation.currentSelection.id ?? null : null;
      const key = orientationId ? `${productId}-${orientationId}` : productId;
      if (sceneOrder[key]) {
        sceneOrder[key].quantity += 1;
      } else {
        sceneOrder[key] = {
          id: productId,
          quantity: 1
        };
      }
    });

    // Preserve slipcover entries (not tracked in 3D scene)
    Object.entries(orderStatusRef.current || {}).forEach(([key, value]) => {
      if (!sceneOrder[key]) {
        const productIdFromKey = key.includes('-') ? key.split('-')[0] : key;
        const product = bundleProducts?.find(p => String(p.id) === String(productIdFromKey) && p.type?.name?.toLowerCase().endsWith('slipcover'));
        if (product) {
          sceneOrder[key] = value;
        }
      }
    });
    updateModularOrderStatus(sceneOrder);
  }, [updateModularOrderStatus]);
  const deleteModularOrder = useCallback(() => {
    // Update order status in local storage
    setItemInStorage(MODULAR_ORDER_STATE, {});
    updateModularOrderStatus({});
  }, [updateModularOrderStatus]);
  const updateModularTotalPrice = () => {
    if (!modularBundleOrderStatus) {
      return;
    }
    const {
      bundleProducts
    } = modularState;
    let updatedPrice = 0;
    Object.keys(modularBundleOrderStatus).forEach(key => {
      const addedProduct = modularBundleOrderStatus[key];
      const productId = key.includes('-') ? key.split('-')[0] : addedProduct.id;
      const product = bundleProducts.find(prod => parseInt(prod.id, 10) === parseInt(productId, 10));
      if (product) {
        const productPrice = getFinalPrice({
          bundleMeta: product.meta,
          options,
          selectedOptions
        });
        updatedPrice += (productPrice?.price || 0) * modularBundleOrderStatus[key].quantity;
      }
    });
    updateModularOrderTotal(updatedPrice);
  };
  const addModularToCart = async ({
    decideLater
  }) => {
    if (!Object.keys(modularBundleOrderStatus || {}).length || !modularTotalPrice) {
      return;
    }
    setAddingToCart(true);
    const {
      bundleProducts
    } = modularState;
    const updatedBundleOrder = [];
    // Build product object for each select bundle product
    Object.keys(modularBundleOrderStatus).forEach(key => {
      const addedProduct = modularBundleOrderStatus[key];
      const orientationId = key.includes('-') ? key.split('-')[1] : null;
      const product = bundleProducts.find(prod => {
        if (parseInt(prod.id, 10) === parseInt(addedProduct.id, 10)) {
          if (!isEmpty(prod.orientation)) {
            if (parseInt(prod.orientation.currentSelection.id, 10) === parseInt(orientationId, 10)) {
              return true;
            }
            return false;
          }
          return true;
        }
        return null;
      });
      let orientationOptionValue = null;

      // Get orientation option if any
      if (orientationId) {
        orientationOptionValue = {
          option_id: product.orientation.option_id,
          option_value_id: parseInt(orientationId, 10)
        };
      }
      const {
        productOptions
      } = getProductOptionsForAddToCart({
        selectedOptions,
        options: product.options,
        addlProdOption: orientationOptionValue
      });
      const productOrder = {
        id: addedProduct.id,
        quantity: addedProduct.quantity,
        sku: '',
        imageSKU: '',
        pickLater: decideLater,
        productOptions
      };
      if (orientationId && !isEmpty(product.orientation) && product.orientation.option && product.orientation.option.sku) {
        productOrder.sku = `${productOrder.sku}${product.orientation.option.sku}`;
      }
      updatedBundleOrder.push(productOrder);
    });
    try {
      addMultipleToCart({
        variables: {
          products: JSON.stringify(updatedBundleOrder),
          zipCode: getZipcode()
        }
      });
    } catch (e) {
      logError(e);
    }
  };
  useEffect(() => {
    if (!cartData || !addingToCart) {
      return;
    }
    const cart = get(cartData, 'getCart.cart', []);
    if (cart.length) {
      toggleCart();
      setItemInStorage(MODULAR_ORDER_STATE, {});
      updateModularOrderStatus({});
      Object.values(modularBundleOrderStatus).forEach(bundleItem => {
        const product = find(modularState?.bundleProducts, {
          id: bundleItem.id
        });

        // TODO: Proper handling of events for Modular products needs to be done
        // FabricName, FabricColor and fullPrice cannot be easily fetched. The same
        // individual product hooks cannot be re-used here
        avo.productAdded({
          productId: +bundleItem.id,
          nonInteraction: 1,
          price: 0,
          fullPrice: parseFloat(product?.price),
          quantity: +bundleItem.quantity,
          sku: product?.sku,
          cartId: cartData?.getCart?.cart_token,
          name: product?.name,
          variant: '',
          quickship: false,
          fabricName: '',
          fabricColor: '',
          location: avo.Location.PDP,
          component: undefined,
          currency: 'USD'
        });
      });
    }
    setAddingToCart(false);
    // TODO: Update useModularPDPState to return functions
    // with stable identities, then add useModularPDPState to dependency array
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addingToCart, cartData, toggleCart]);
  useEffect(() => {
    if (!modularBundleOrderStatus || isEmpty(selectedOptions) || isEmpty(modularBundleOrderStatus) && !modularTotalPrice) {
      return;
    }
    updateModularTotalPrice();
    // TODO: Refactor this hook to return a stable identity for
    // updateModularTotalPrice, then add updateModularTotalPrice to dependency
    // array
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modularBundleOrderStatus, modularTotalPrice, selectedOptions]);
  return {
    loading: optionsLoading || modularPDPLoading,
    data: {
      addingToCart,
      modularBundleOrderStatus,
      modularTotalPrice
    },
    actions: {
      updateModularOrder,
      addModularToCart,
      deleteModularOrder,
      rebuildOrderFromScene
    }
  };
};
export default useModularPDPCart;